import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { Link } from "@/i18n/LocaleLink";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";
import { GUIDES, GUIDE_COPY, guideById, resolveGuideAsset } from "@/data/guides";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Resolve cover/pdf for the active language (Dutch files slot in later,
   English fallback until then — see data/guides.js). */
const useGuideAssets = (guide) => {
  const { lang } = useLang();
  const [assets, setAssets] = useState({ cover: guide.cover.en, pdf: guide.pdf.en, lang });
  useEffect(() => {
    let live = true;
    if (lang === "nl") {
      Promise.all([resolveGuideAsset(guide, "nl", "cover"), resolveGuideAsset(guide, "nl", "pdf")]).then(
        ([cover, pdf]) => live && setAssets({ cover, pdf, lang })
      );
    } else {
      setAssets({ cover: guide.cover.en, pdf: guide.pdf.en, lang });
    }
    return () => {
      live = false;
    };
  }, [guide, lang]);
  return assets.lang === lang ? assets : { cover: guide.cover.en, pdf: guide.pdf.en, lang };
};

/* Inline request form: email only, optional unticked Slow Letter checkbox. */
const GuideRequestForm = ({ guide, t, lang }) => {
  const [email, setEmail] = useState("");
  const [slowLetter, setSlowLetter] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot — hidden from humans
  const [sending, setSending] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [error, setError] = useState(false);
  const assets = useGuideAssets(guide);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    setError(false);
    try {
      const { data } = await axios.post(`${API}/guides/request`, {
        guide: guide.id,
        email,
        lang,
        slow_letter: slowLetter,
        website,
      });
      setDownloadUrl(data.download_url || assets.pdf);
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  if (downloadUrl) {
    return (
      <div className="mt-5" data-testid={`guide-success-${guide.id}`}>
        <p className="text-sm leading-relaxed text-soft">{t.success}</p>
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`guide-open-${guide.id}`}
          className="group mt-3 inline-flex items-center gap-2 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust"
        >
          {t.open}
          <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-5 w-full max-w-md" data-testid={`guide-form-${guide.id}`}>
      <div className="flex items-center border-b border-ink/30 transition-colors duration-300 focus-within:border-clay">
        <label htmlFor={`guide-email-${guide.id}`} className="sr-only">{t.emailLabel}</label>
        <input
          id={`guide-email-${guide.id}`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.emailPlaceholder}
          aria-label={t.emailLabel}
          data-testid={`guide-email-input-${guide.id}`}
          className="flex-1 bg-transparent py-3 text-base text-ink outline-none placeholder:text-soft/60"
        />
        <button
          type="submit"
          disabled={sending}
          data-testid={`guide-submit-${guide.id}`}
          className="group flex items-center gap-2 py-3 pl-4 text-sm tracking-wide text-clay disabled:opacity-50"
        >
          {sending ? t.sending : t.send}
          <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
      <label className="mt-4 flex cursor-pointer items-start gap-3" data-testid={`guide-slowletter-label-${guide.id}`}>
        <input
          type="checkbox"
          checked={slowLetter}
          onChange={(e) => setSlowLetter(e.target.checked)}
          data-testid={`guide-slowletter-${guide.id}`}
          className="mt-1 h-4 w-4 shrink-0 accent-clay"
        />
        <span className="text-sm leading-relaxed text-soft">{t.slowLetter}</span>
      </label>
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} data-testid={`guide-honeypot-${guide.id}`} />
      </div>
      {error && <p className="mt-3 text-sm text-rust" data-testid={`guide-error-${guide.id}`}>{t.error}</p>}
      <p className="mt-3 text-xs leading-relaxed text-soft/70" data-testid={`guide-privacy-note-${guide.id}`}>
        {t.privacyNote}{" "}
        <Link to="/privacy" className="underline underline-offset-2 transition-colors duration-300 hover:text-clay" data-testid={`guide-privacy-link-${guide.id}`}>
          {t.privacyLink}
        </Link>
      </p>
    </form>
  );
};

/* One guide: cover, title, subtitle, receive button → inline form. */
const GuideRow = ({ guide, t, lang }) => {
  const [open, setOpen] = useState(false);
  const assets = useGuideAssets(guide);
  return (
    <div className="flex flex-col gap-6 sm:flex-row sm:gap-8" data-testid={`guide-row-${guide.id}`}>
      <div className="w-28 shrink-0 overflow-hidden rounded-sm border border-ink/10 shadow-sm">
        <img loading="lazy" src={assets.cover} alt={guide.title[lang]} className="w-full" data-testid={`guide-cover-${guide.id}`} />
      </div>
      <div className="flex-1">
        <h3 className="font-serif text-xl sm:text-2xl tracking-tight text-ink">{guide.title[lang]}</h3>
        <p className="mt-1.5 text-sm text-soft">
          {guide.sub[lang]}
          {lang === "nl" && assets.pdf === guide.pdf.en && <span className="text-soft/60"> · {t.englishNote}</span>}
        </p>
        {!open ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid={`guide-receive-${guide.id}`}
            className="group mt-4 inline-flex items-center gap-2 rounded-full border border-clay px-6 py-2.5 text-sm tracking-wide text-clay transition-colors duration-300 hover:bg-clay hover:text-cream"
          >
            {t.receive}
          </button>
        ) : (
          <GuideRequestForm guide={guide} t={t} lang={lang} />
        )}
      </div>
    </div>
  );
};

/* Inline rows without section/heading — for embedding inside an existing section. */
export const GuideInline = ({ ids, testid = "guide-inline" }) => {
  const { lang } = useLang();
  const t = GUIDE_COPY[lang];
  const guides = ids.map(guideById).filter(Boolean);
  if (guides.length === 0) return null;
  return (
    <div className="space-y-12" data-testid={testid}>
      {guides.map((g) => (
        <GuideRow key={g.id} guide={g} t={t} lang={lang} />
      ))}
    </div>
  );
};

/* Contextual block for threshold & offering pages: heading + guide rows. */
export const GuideBlock = ({ ids, bg = "bg-parchment", testid = "guide-block" }) => {
  const { lang } = useLang();
  const t = GUIDE_COPY[lang];
  const guides = ids.map(guideById).filter(Boolean);
  if (guides.length === 0) return null;
  return (
    <section className={`${bg} border-t border-ink/10 py-20 lg:py-28`} data-testid={testid}>
      <div className="mx-auto max-w-3xl px-6">
        <Reveal>
          <h2 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl" data-testid={`${testid}-heading`}>{t.heading}</h2>
        </Reveal>
        <div className="mt-10 space-y-12">
          {guides.map((g) => (
            <Reveal key={g.id}>
              <GuideRow guide={g} t={t} lang={lang} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* Homepage section: quiet 4-card collection before the Journal. */
export const GuideCollection = () => {
  const { lang } = useLang();
  const t = GUIDE_COPY[lang];
  return (
    <section className="bg-parchment py-28 lg:py-36" data-testid="section-resources">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <h2 className="font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl" data-testid="resources-heading">{t.heading}</h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-soft" data-testid="resources-intro">{t.intro}</p>
        </Reveal>
        <div className="mt-14 grid gap-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {GUIDES.map((g, i) => (
            <Reveal key={g.id} delay={i * 0.08}>
              <GuideCard guide={g} t={t} lang={lang} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const GuideCard = ({ guide, t, lang }) => {
  const [open, setOpen] = useState(false);
  const assets = useGuideAssets(guide);
  return (
    <div data-testid={`resource-card-${guide.id}`}>
      <div className="overflow-hidden rounded-sm border border-ink/10 shadow-sm">
        <img loading="lazy" src={assets.cover} alt={guide.title[lang]} className="aspect-[953/1348] w-full object-cover" data-testid={`resource-cover-${guide.id}`} />
      </div>
      <h3 className="mt-5 font-serif text-xl leading-snug tracking-tight text-ink">{guide.title[lang]}</h3>
      <p className="mt-1.5 text-sm text-soft">
        {guide.sub[lang]}
        {lang === "nl" && assets.pdf === guide.pdf.en && <span className="text-soft/60"> · {t.englishNote}</span>}
      </p>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`guide-receive-${guide.id}`}
          className="group mt-4 inline-flex items-center gap-2 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust"
        >
          {t.receive}
          <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      ) : (
        <GuideRequestForm guide={guide} t={t} lang={lang} />
      )}
    </div>
  );
};
